
import React, { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, UserPlus, UserX, PencilIcon } from 'lucide-react';
import { SendInviteDialog } from './members/SendInviteDialog';
import { InvitationsList } from './members/InvitationsList';
import EditMemberDialog from './members/EditMemberDialog';
import RemoveMemberDialog from './members/RemoveMemberDialog';
import CompanyMemberCard from './CompanyMemberCard';
import { removeUserFromCompany, updateUserCompanyRole } from '@/services/userCompanyService';
import { getCompanyMembers } from '@/services/companyMemberService';
import { UserRole } from '@/lib/types';
import { useAuth } from '@/context/AuthContext';

interface CompanyMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  companyRole: string;
}

interface CompanyMembersTabProps {
  companyId: string;
  isAdmin: boolean;
}

const CompanyMembersTab: React.FC<CompanyMembersTabProps> = ({ companyId, isAdmin }) => {
  const { toast } = useToast();
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<CompanyMember | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const { profile } = useAuth();

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      const members = await getCompanyMembers(companyId);
      setMembers(members);
    } catch (error) {
      console.error('Error fetching company members:', error);
      toast({
        title: "Error",
        description: "Failed to load company members.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [companyId]);

  const handleOpenEdit = (member: CompanyMember) => {
    setSelectedMember(member);
    setIsEditOpen(true);
  };

  const handleOpenRemove = (member: CompanyMember) => {
    setSelectedMember(member);
    setIsRemoveOpen(true);
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      // Convert the string role to UserRole type
      const userRole = role as UserRole;
      await updateUserCompanyRole(userId, companyId, userRole);
      await fetchMembers();
      toast({
        title: "Role updated",
        description: "Member role has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: "Error",
        description: "Failed to update member role.",
        variant: "destructive"
      });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeUserFromCompany(userId, companyId);
      await fetchMembers();
      setIsRemoveOpen(false);
      toast({
        title: "Member removed",
        description: "Member has been removed from the company.",
      });
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: "Failed to remove member.",
        variant: "destructive"
      });
    }
  };

  const columns = [
    {
      header: "User",
      accessorKey: "id",
      cell: ({ row }: { row: any }) => {
        const user = row.original;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.avatar_url || ''} alt={`${user.first_name} ${user.last_name}`} />
              <AvatarFallback>
                {user.first_name && user.last_name 
                  ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase() 
                  : 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{user.first_name} {user.last_name}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      }
    },
    {
      header: "Role",
      accessorKey: "companyRole",
      cell: ({ row }: { row: any }) => {
        const role = row.original.companyRole;
        return (
          <Badge variant={role === 'admin' ? 'default' : 'secondary'}>
            {role}
          </Badge>
        );
      }
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }: { row: any }) => {
        return isAdmin ? (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleOpenEdit(row.original)}
            >
              <PencilIcon className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleOpenRemove(row.original)}
            >
              <UserX className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        ) : null;
      }
    }
  ];

  return (
    <div className="space-y-4">
      {(isAdmin || profile?.role === 'manager') && (
        <div className="flex justify-end">
          <Button onClick={() => setIsInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        </div>
      )}

      <InvitationsList companyId={companyId} />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <DataTable
              columns={columns}
              data={members}
            />
          </div>
          
          {/* Mobile/Tablet Card View */}
          <div className="lg:hidden space-y-3">
            {members.map((member) => (
              <CompanyMemberCard
                key={member.id}
                member={member}
                isAdmin={isAdmin}
                onEdit={handleOpenEdit}
                onRemove={handleOpenRemove}
              />
            ))}
          </div>
        </>
      )}

      <SendInviteDialog
        companyId={companyId}
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        onSuccess={fetchMembers}
      />

      {selectedMember && (
        <>
          <EditMemberDialog
            open={isEditOpen}
            onOpenChange={setIsEditOpen}
            member={selectedMember}
            companyId={companyId}
            onSuccess={fetchMembers}
          />

          <RemoveMemberDialog
            open={isRemoveOpen}
            onOpenChange={setIsRemoveOpen}
            member={selectedMember}
            onConfirm={() => handleRemoveMember(selectedMember.id)}
          />
        </>
      )}
    </div>
  );
};

export default CompanyMembersTab;
